// Manager EOD Report — daily end-of-day dashboard strictly scoped to the
// signed-in Manager's own projects and work orders. Used in two modes:
//   - embedded: the Manager's main dashboard (inside the app Layout)
//   - bare: /dashboard/report — full-page, white, print/screenshot-optimised
import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { StatusBadge } from "@/lib/statusColor";
import { Printer, FileText, AlertTriangle } from "lucide-react";

const TODAY = new Date().toISOString().slice(0, 10);
const isToday = (iso) => typeof iso === "string" && iso.slice(0, 10) === TODAY;
const isOverdue = (w) => w.deadline && w.deadline < TODAY && !["Completed", "Cancelled"].includes(w.status);

function currentPhaseName(project) {
  const phases = project.phases || [];
  if (!phases.length) return "—";
  if (project.status === "Completed") return phases[phases.length - 1]?.name || "—";
  const active = phases.findIndex((p) => (!p.start_date || p.start_date <= TODAY) && (!p.end_date || p.end_date >= TODAY));
  if (active !== -1) return phases[active]?.name;
  const past = phases.filter((p) => p.end_date && p.end_date < TODAY).length;
  return phases[Math.min(past, phases.length - 1)]?.name || "—";
}

function EodCard({ title, rows, testid }) {
  return (
    <div className="bg-white border border-black/10 rounded-xl p-4 print:shadow-none print:rounded-lg" data-testid={testid}>
      <div className="label-caps text-royal-800">{title}</div>
      <div className="mt-2 space-y-1">
        {rows.map(([label, value, warn]) => (
          <div key={label} className="flex items-baseline justify-between text-sm">
            <span className="text-ink/70">{label}</span>
            <span className={`font-mono-tight font-semibold ${warn ? "text-[#B02A00]" : "text-royal-800"}`}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ManagerEodReport({ bare = false }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [wos, setWos] = useState(null);
  const [freelancers, setFreelancers] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, w, f, pay] = await Promise.all([
          api.get("/projects"),
          api.get("/work-orders"),
          api.get("/freelancers").catch(() => ({ data: [] })),
          api.get("/payments").catch(() => ({ data: [] })),
        ]);
        setProjects(p.data); setWos(w.data); setFreelancers(f.data); setPayments(pay.data);
      } catch { setProjects([]); setWos([]); }
    })();
  }, []);

  const m = useMemo(() => {
    if (!projects || !wos) return null;
    const myPids = new Set(projects.map((p) => p.id));
    const pmap = Object.fromEntries(projects.map((p) => [p.id, p]));
    const fmap = Object.fromEntries(freelancers.map((f) => [f.id, f]));

    // -- Projects --
    const active = projects.filter((p) => (p.status || "") !== "Completed");
    const overdueProjects = projects.filter((p) => p.due_date && p.due_date < TODAY && p.status !== "Completed");
    const onHold = projects.filter((p) => p.status === "On Hold");
    const onTrack = active.filter((p) => !(p.due_date && p.due_date < TODAY) && p.status !== "On Hold");

    // -- Work orders (already manager-scoped by the API) --
    const open = wos.filter((w) => !["Completed", "Cancelled"].includes(w.status));
    const submittedToday = wos.filter((w) => w.status === "Submitted" && isToday(w.updated_at));
    const completedToday = wos.filter((w) => w.status === "Completed" && isToday(w.updated_at));
    const awaitingReview = wos.filter((w) => w.status === "Submitted");
    const overdue = wos.filter(isOverdue);
    const dueToday = wos.filter((w) => w.deadline === TODAY);
    const dueTodayDone = dueToday.filter((w) => w.status === "Completed");

    // -- Freelancers assigned to my projects --
    const assignedFlIds = [...new Set(wos.map((w) => w.freelancer_id).filter(Boolean))];
    const flActivity = assignedFlIds.map((fid) => {
      const mine = wos.filter((w) => w.freelancer_id === fid);
      return {
        id: fid,
        name: fmap[fid]?.name || mine[0]?.freelancer_name || "Freelancer",
        assigned: mine.filter((w) => !["Completed", "Cancelled"].includes(w.status)).length,
        submittedToday: mine.filter((w) => w.status === "Submitted" && isToday(w.updated_at)).length,
        completed: mine.filter((w) => w.status === "Completed").length,
        overdue: mine.filter(isOverdue).length,
      };
    }).sort((a, b) => b.overdue - a.overdue || b.assigned - a.assigned);

    // -- Pending payouts against my projects only --
    const woMap = Object.fromEntries(wos.map((w) => [w.id, w]));
    const pendingPayout = payments
      .filter((p) => myPids.has(p.project_id) || (p.work_order_id && woMap[p.work_order_id]))
      .filter((p) => ["Pending", "Partially Paid"].includes(p.status))
      .reduce((sum, p) => sum + Math.max(0, (Number(p.amount) || 0) - (Number(p.amount_paid) || 0)), 0);

    // -- WO groups by status --
    const groups = [
      ["Overdue", overdue],
      ["Submitted", awaitingReview],
      ["In Progress", wos.filter((w) => w.status === "In Progress" && !isOverdue(w))],
      ["Assigned", wos.filter((w) => w.status === "Assigned" && !isOverdue(w))],
      ["Completed", wos.filter((w) => w.status === "Completed")],
    ];

    // -- Projects progressed today: phase whose window covers today, or status change today --
    const progressedToday = projects.filter((p) =>
      (p.phases || []).some((ph) => (!ph.start_date || ph.start_date <= TODAY) && (!ph.end_date || ph.end_date >= TODAY))
    );

    return {
      active, overdueProjects, onHold, onTrack,
      open, submittedToday, completedToday, awaitingReview, overdue, dueToday, dueTodayDone,
      flActivity, pendingPayout, groups, progressedToday, pmap,
    };
  }, [projects, wos, freelancers, payments]);

  if (!m) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading report…</div>;

  const dateLabel = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const flName = (w) => w.freelancer_name || freelancers.find((f) => f.id === w.freelancer_id)?.name || "—";

  return (
    <div data-testid="eod-report" className={bare ? "min-h-screen bg-white" : "p-8 pt-6"}>
      {/* Header */}
      <div className={`${bare ? "max-w-5xl mx-auto px-8 py-10" : ""}`}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b-2 border-royal-800 pb-4">
          <div>
            <div className="label-caps text-royal-800">FreLancTrac · Manager EOD</div>
            <h1 className="font-display text-3xl mt-1" data-testid="eod-title">
              Daily Report — {user.name} — {dateLabel}
            </h1>
            <div className="text-xs text-neutral-500 font-mono-tight mt-1">
              Scope: only projects and work orders assigned to {user.name}
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
            {!bare && (
              <Link to="/dashboard/report" data-testid="eod-view-full-report-btn" className="btn-outline inline-flex items-center gap-2">
                <FileText size={14} /> View Full Report
              </Link>
            )}
            <button data-testid="eod-print-btn" onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2">
              <Printer size={14} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="eod-cards">
          <EodCard testid="eod-card-projects" title="Active Projects" rows={[
            ["Total", m.active.length],
            ["On track", m.onTrack.length],
            ["Overdue", m.overdueProjects.length, m.overdueProjects.length > 0],
            ["On hold", m.onHold.length],
          ]} />
          <EodCard testid="eod-card-workorders" title="Work Orders" rows={[
            ["Open", m.open.length],
            ["Submitted today", m.submittedToday.length],
            ["Completed today", m.completedToday.length],
            ["Awaiting review", m.awaitingReview.length],
            ["Overdue", m.overdue.length, m.overdue.length > 0],
          ]} />
          <EodCard testid="eod-card-today" title="Today's Tasks" rows={[
            ["Due today", m.dueToday.length],
            ["Completed", m.dueTodayDone.length],
            ["Pending", m.dueToday.length - m.dueTodayDone.length, (m.dueToday.length - m.dueTodayDone.length) > 0],
          ]} />
          <EodCard testid="eod-card-freelancers" title="Active Freelancers" rows={[
            ["On my projects", m.flActivity.length],
            ["With overdue WOs", m.flActivity.filter((f) => f.overdue > 0).length, m.flActivity.some((f) => f.overdue > 0)],
          ]} />
          <EodCard testid="eod-card-payouts" title="Pending Payouts" rows={[
            ["Amount", formatINR(m.pendingPayout), m.pendingPayout > 0],
            ["Scope", "my projects"],
          ]} />
        </div>

        {/* Work orders grouped by status */}
        <div className="mt-8" data-testid="eod-wos">
          <div className="label-caps text-royal-800 mb-2">Work Orders by Status</div>
          <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
            <table className="fms-table w-full">
              <thead><tr><th>Work Order</th><th>Freelancer</th><th>Project</th><th>Deadline</th><th>Status</th></tr></thead>
              <tbody>
                {m.groups.map(([label, rows]) => (
                  rows.length > 0 && (
                    <Fragment key={label}>
                      <tr className="bg-cream-2/60">
                        <td colSpan={5} className="label-caps !py-2" data-testid={`eod-wo-group-${label.replace(/ /g, "-").toLowerCase()}`}>
                          {label} ({rows.length})
                        </td>
                      </tr>
                      {rows.map((w) => (
                        <tr key={w.id} data-testid={`eod-wo-${w.id}`}>
                          <td className="font-medium">{w.title}</td>
                          <td className="text-sm">{flName(w)}</td>
                          <td className="text-sm text-ink/70">{m.pmap[w.project_id]?.name || "—"}</td>
                          <td className="font-mono-tight text-xs">{w.deadline || "—"}</td>
                          <td><StatusBadge status={label === "Overdue" ? "Overdue" : w.status} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  )
                ))}
                {wos.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No work orders.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Projects */}
        <div className="mt-8" data-testid="eod-projects">
          <div className="label-caps text-royal-800 mb-2">My Projects</div>
          <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
            <table className="fms-table w-full">
              <thead><tr><th>Project</th><th>Number</th><th>Current Phase</th><th>Status</th><th>Deadline</th></tr></thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} data-testid={`eod-project-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td className="font-mono-tight text-xs">{p.project_number || "—"}</td>
                    <td className="text-sm">{currentPhaseName(p)}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="font-mono-tight text-xs">{p.due_date || "—"}</td>
                  </tr>
                ))}
                {projects.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No projects assigned.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Freelancer activity */}
        <div className="mt-8" data-testid="eod-freelancers">
          <div className="label-caps text-royal-800 mb-2">Freelancer Activity</div>
          <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
            <table className="fms-table w-full">
              <thead><tr><th>Freelancer</th><th>WOs Assigned</th><th>Submitted Today</th><th>Completed</th><th>Overdue</th></tr></thead>
              <tbody>
                {m.flActivity.map((f) => (
                  <tr key={f.id} data-testid={`eod-fl-${f.id}`}>
                    <td className="font-medium">{f.name}</td>
                    <td className="font-mono-tight">{f.assigned}</td>
                    <td className="font-mono-tight">{f.submittedToday}</td>
                    <td className="font-mono-tight">{f.completed}</td>
                    <td className={`font-mono-tight ${f.overdue > 0 ? "text-[#B02A00] font-semibold" : ""}`}>{f.overdue}</td>
                  </tr>
                ))}
                {m.flActivity.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No freelancers assigned to your projects yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* EOD summary */}
        <div className="mt-8 mb-4" data-testid="eod-summary">
          <div className="label-caps text-royal-800 mb-2">EOD Summary</div>
          <div className="bg-royal-800 text-cream rounded-xl p-5 space-y-2 text-sm">
            <div><b>{m.completedToday.length}</b> work order{m.completedToday.length === 1 ? "" : "s"} completed today.</div>
            <div><b>{m.open.length}</b> work order{m.open.length === 1 ? "" : "s"} still pending ({m.awaitingReview.length} awaiting your review).</div>
            <div><b>{m.progressedToday.length}</b> project{m.progressedToday.length === 1 ? "" : "s"} progressed today (phase in flight).</div>
            {m.overdue.length > 0 || m.overdueProjects.length > 0 ? (
              <div className="flex items-start gap-2 text-[#FFC7A6]">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>Needs attention: <b>{m.overdue.length}</b> overdue work order{m.overdue.length === 1 ? "" : "s"} · <b>{m.overdueProjects.length}</b> overdue project{m.overdueProjects.length === 1 ? "" : "s"}.</span>
              </div>
            ) : (
              <div className="text-[#9BD3AE]">No overdue items — everything is on track.</div>
            )}
            {m.onHold.length > 0 && (
              <div className="text-gold"><b>{m.onHold.length}</b> project{m.onHold.length === 1 ? "" : "s"} on hold: {m.onHold.map((p) => p.name).join(", ")}.</div>
            )}
          </div>
        </div>

        {bare && (
          <div className="text-center text-xs text-neutral-400 font-mono-tight pb-10 print:pb-0">
            Generated from FreLancTrac · {new Date().toLocaleString("en-GB")}
          </div>
        )}
      </div>
    </div>
  );
}
